export const GTIN_CHECK_ERROR_CATALOGUE = {
    0: {
        code: 0,
        message: 'INTEGRITY GOOD',
        advice: 'Valid structure and check digit.',
    },
    102: {
        code: 102,
        message: 'Alphabetic digits found',
        advice: 'Barcodes can only be constructed with numeric values. Ensure that there are no leading or trailing spaces',
    },
    103: {
        code: 103,
        message: 'Incorrect check digit',
        advice: "Each barcode ends with a check digit that is computed from the preceding numbers. Check the sequence of digits has been accurately typed or you can use GS1 UK's check digit calculator",
    },
    104: {
        code: 104,
        message: 'Invalid length',
        advice: 'Barcodes are a standard length of 8, 12, 13 or 14 digits. Ensure that there are no leading or trailing spaces or extra numbers',
    },
    201: {
        code: 201,
        message: 'Number cannot be licensed for use in open global supply chains',
        advice: 'This barcode includes a restricted prefix that makes it unsuitable for retail products.',
    },
    202: {
        code: 202,
        message: 'Number prefix reserved for EPC (RFID)',
        advice: 'This barcode includes a prefix that is reserved for creating Electronic Product Codes (EPC) for programming RFID tags',
    },
    203: {
        code: 203,
        message: 'Number prefix reserved for demonstration purposes only',
        advice: 'This barcode includes a prefix that is reserved for creating numbers to be used within training and reference materials.',
    },
    204: {
        code: 204,
        message: 'Number prefix reserved for serial publications',
        advice: 'This barcode uses a prefix reserved for use with the International Standard Serial Number (ISSN) system for periodicals such as magazines, journals and newspapers.',
    },
    205: {
        code: 205,
        message: 'Number prefix reserved for books',
        advice: 'This barcode uses a prefix reserved for use with the International Standard Book Number (ISBN) system for books, ebooks and related products.',
    },
    206: {
        code: 206,
        message: 'Number is not licensed',
        advice: 'The prefix used for this barcode is eligible for licensing, however GS1 records show that it is currently unlicensed.',
    },
    207: {
        code: 207,
        message: 'Number prefix reserved for couponing',
        advice: 'The prefix used for this barcode is reserved for creating coupons.',
    },
    208: {
        code: 208,
        message: 'Number prefix reserved for couponing',
        advice: 'The prefix used for this barcode is reserved for creating coupons.',
    },
    209: {
        code: 209,
        message: 'Number prefix reserved for receipts',
        advice: 'The prefix used for this barcode is reserved for creating receipts',
    },
    210: {
        code: 210,
        message: 'Number prefix reserved',
        advice: 'The prefix used for this barcode has been reserved by GS1 for future applications.',
    },
    301: {
        code: 301,
        message: 'Number is not licensed',
        advice: 'The prefix used for this barcode is eligible for licensing, however GS1 records show that it is currently unlicensed.',
    },
    302: {
        code: 302,
        message: 'Number is not licensed',
        advice: 'The prefix used for this barcode is eligible for licensing, however GS1 records show that it is currently unlicensed.',
    },
    303: {
        code: 303,
        message: 'Number prefix reserved',
        advice: 'The prefix used for this barcode has been reserved by GS1 for future applications.',
    },
    304: {
        code: 304,
        message: 'This number is registered to this company although no product information has been provided',
        advice: 'This prefix has been licensed to a member company.',
    },
    305: {
        code: 305,
        message: 'This number is registered to this company, although only limited information is available',
        advice: 'This barcode is in use and is associated with a specific product.',
    },
    306: {
        code: 306,
        message: 'Complete data is registered to this company',
        advice: 'This barcode is in use and is associated with a specific product.',
    },
};
export function lookupGtinCheckErrorCode(code) {
    if (typeof code !== 'number' || !Number.isInteger(code)) {
        return null;
    }
    return GTIN_CHECK_ERROR_CATALOGUE[code] ?? null;
}
/**
 * Formats an identifier's `gs1Check` block into plain language for the Admin UI product screen
 * (Spec v2.1 §22.12, WP-91; GS1 UK GTIN Check API Developers' guide v5.0 p4 & Appendix 3 pp. 14–15).
 */
export function formatPlainGtinCheckSummary(gs1Check) {
    if (gs1Check === undefined) {
        return {
            badgeLabel: 'Format valid · Not checked with GS1 UK',
            tone: 'neutral',
            headline: 'Barcode format and check digit are valid.',
            detail: 'Live GS1 UK registry check has not run for this product yet.',
            advice: null,
            ownerWarning: null,
        };
    }
    const appendixEntry = lookupGtinCheckErrorCode(gs1Check.errorCode ?? gs1Check.integrityCode);
    const advice = gs1Check.adviceText ?? appendixEntry?.advice ?? null;
    const ownerWarning = gs1Check.ownerMismatch === true
        ? gs1Check.gcpOwner
            ? `This barcode prefix is registered to ${gs1Check.gcpOwner}, which does not match your workspace organisation.`
            : 'This barcode prefix is registered to a different organisation in the GS1 registry.'
        : null;
    if (gs1Check.verifiedByChoice === 'UNAVAILABLE' || gs1Check.checkError) {
        return {
            badgeLabel: 'GS1 UK check unavailable',
            tone: 'neutral',
            headline: 'Barcode format and check digit are valid, but the live GS1 UK registry check could not be completed right now.',
            detail: 'Saving your product was not blocked.',
            advice: null,
            ownerWarning: null,
        };
    }
    const certainty = gs1Check.certaintyValue;
    if (certainty === 3) {
        const ownerPart = gs1Check.gcpOwner
            ? `Registered to ${gs1Check.gcpOwner}`
            : 'Registered in GS1';
        const territoryPart = gs1Check.gs1Territory ? ` (${gs1Check.gs1Territory})` : '';
        const productPart = gs1Check.brandName && gs1Check.productDescription
            ? `${gs1Check.brandName} — ${gs1Check.productDescription}`
            : (gs1Check.productDescription ?? gs1Check.brandName ?? null);
        return {
            badgeLabel: gs1Check.ownerMismatch ? 'Prefix owner mismatch' : 'Verified in GS1 registry',
            tone: gs1Check.ownerMismatch ? 'warning' : 'success',
            headline: `${ownerPart}${territoryPart} · Product record found in the GS1 registry.`,
            detail: productPart,
            advice,
            ownerWarning,
        };
    }
    if (certainty === 2) {
        const ownerPart = gs1Check.gcpOwner
            ? `Company prefix registered to ${gs1Check.gcpOwner}`
            : 'Company prefix verified in the GS1 License Registry';
        const territoryPart = gs1Check.gs1Territory ? ` (${gs1Check.gs1Territory})` : '';
        return {
            badgeLabel: gs1Check.ownerMismatch ? 'Prefix owner mismatch' : 'Company prefix verified',
            tone: gs1Check.ownerMismatch ? 'warning' : 'success',
            headline: `${ownerPart}${territoryPart}.`,
            detail: 'No individual product description has been published to the GS1 Product Registry yet.',
            advice,
            ownerWarning,
        };
    }
    if (certainty === 1) {
        const errText = gs1Check.errorMessage ?? appendixEntry?.message ?? null;
        return {
            badgeLabel: 'Unrecognised company prefix',
            tone: 'warning',
            headline: errText ??
                'Barcode passes check digit validation, but no matching company prefix was found in the GS1 License Registry.',
            detail: gs1Check.gcpCode ? `Checked prefix: ${gs1Check.gcpCode}` : null,
            advice,
            ownerWarning,
        };
    }
    if (certainty === 0 || !gs1Check.checkDigitValid) {
        const errText = gs1Check.errorMessage ??
            appendixEntry?.message ??
            gs1Check.integrityCheck ??
            'This barcode failed the GS1 integrity check.';
        return {
            badgeLabel: 'Failed GS1 integrity check',
            tone: 'destructive',
            headline: errText,
            detail: null,
            advice,
            ownerWarning,
        };
    }
    if (gs1Check.verifiedByChoice === 'VERIFIED') {
        return {
            badgeLabel: 'Prefix verified locally',
            tone: 'success',
            headline: gs1Check.gs1Prefix
                ? `Matches your workspace GS1 Company Prefix (${gs1Check.gs1Prefix}).`
                : 'Matches your workspace GS1 Company Prefix.',
            detail: 'Valid GS1 check digit and format.',
            advice: null,
            ownerWarning: null,
        };
    }
    return {
        badgeLabel: 'Format valid · Not checked with GS1 UK',
        tone: 'neutral',
        headline: 'Barcode format and check digit are valid.',
        detail: gs1Check.gs1Prefix
            ? `Prefix ${gs1Check.gs1Prefix} (live GS1 UK registry check off)`
            : 'Live GS1 UK registry check is currently off.',
        advice: null,
        ownerWarning: null,
    };
}
//# sourceMappingURL=gtin-check.js.map